import MoreVertIcon from "@mui/icons-material/MoreVertOutlined";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {
  Button,
  DataTable,
  FullPageStatus,
  Input,
  Menu,
  showToast,
  type DataTableColumn,
} from "@vv/ui";
import {
  addTeamMember,
  fetchTeams,
  useTeamsDispatch,
  useTeamsSelector,
  type TeamRole,
} from "@vv/teams-frontend";
import { useEffect, useState } from "react";
import { AssignTeamRoleModal } from "../components/AssignTeamRoleModal";
import { InviteUserModal } from "../components/InviteUserModal";
import type { AppUser } from "../domain/user.types";
import { useUsersDispatch, useUsersSelector } from "../state/hooks";
import { fetchUsers, inviteUser, updateUser } from "../state/users.thunks";

export interface UsersListPageProps {
  isOwner: boolean;
  currentUserId: string;
}

export function UsersListPage({ isOwner, currentUserId }: UsersListPageProps) {
  const dispatch = useUsersDispatch();
  const teamsDispatch = useTeamsDispatch();
  const users = useUsersSelector((state) => state.users.users);
  const status = useUsersSelector((state) => state.users.status);
  const error = useUsersSelector((state) => state.users.error);
  const teams = useTeamsSelector((state) => state.teams.teams);
  const [search, setSearch] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuUser, setMenuUser] = useState<AppUser | null>(null);
  const [assignTarget, setAssignTarget] = useState<AppUser | null>(null);

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  useEffect(() => {
    if (isOwner) teamsDispatch(fetchTeams());
  }, [teamsDispatch, isOwner]);

  async function handleSearch(value: string) {
    setSearch(value);
    dispatch(fetchUsers(value || undefined));
  }

  async function toggle(user: AppUser, field: "isActive" | "isOwner" | "isViewer", value: boolean) {
    try {
      await dispatch(updateUser({ id: user.id, payload: { [field]: value } })).unwrap();
      showToast("User updated", "success");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Could not update user", "error");
    }
  }

  async function handleInvite(email: string, displayName: string) {
    try {
      await dispatch(inviteUser({ email, displayName: displayName || undefined })).unwrap();
      showToast("User invited", "success");
      setInviteOpen(false);
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Could not invite user", "error");
      throw err;
    }
  }

  async function handleAssignTeamRole(teamId: string, role: TeamRole) {
    if (!assignTarget) return;
    try {
      await teamsDispatch(
        addTeamMember({ teamId, payload: { userId: assignTarget.id, role } }),
      ).unwrap();
      showToast("Team role assigned", "success");
      setAssignTarget(null);
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Could not assign team role", "error");
      throw err;
    }
  }

  const columns: DataTableColumn<AppUser>[] = [
    { key: "email", header: "Email", render: (user) => user.email, sortable: true },
    { key: "displayName", header: "Name", render: (user) => user.displayName ?? "—" },
    {
      key: "isActive",
      header: "Active",
      render: (user) =>
        isOwner ? (
          <Tooltip
            title={user.id === currentUserId ? "You cannot deactivate your own account" : ""}
          >
            <span>
              <Switch
                size="small"
                checked={user.isActive}
                disabled={user.id === currentUserId}
                onChange={(event) => toggle(user, "isActive", event.target.checked)}
              />
            </span>
          </Tooltip>
        ) : (
          <Chip
            label={user.isActive ? "Active" : "Inactive"}
            size="small"
            color={user.isActive ? "success" : "default"}
          />
        ),
    },
    {
      key: "isOwner",
      header: "Owner",
      render: (user) =>
        isOwner ? (
          <Tooltip
            title={user.id === currentUserId ? "You cannot remove your own Owner status" : ""}
          >
            <span>
              <Switch
                size="small"
                checked={user.isOwner}
                disabled={user.id === currentUserId}
                onChange={(event) => toggle(user, "isOwner", event.target.checked)}
              />
            </span>
          </Tooltip>
        ) : user.isOwner ? (
          <Chip label="Owner" size="small" color="primary" />
        ) : (
          "—"
        ),
    },
    {
      key: "isViewer",
      header: "Viewer",
      render: (user) =>
        isOwner ? (
          <Switch
            size="small"
            checked={user.isViewer}
            onChange={(event) => toggle(user, "isViewer", event.target.checked)}
          />
        ) : user.isViewer ? (
          <Chip label="Viewer" size="small" color="secondary" />
        ) : (
          "—"
        ),
    },
    {
      key: "createdAt",
      header: "Joined",
      render: (user) => new Date(user.createdAt).toLocaleDateString(),
      sortable: true,
      sortValue: (user) => user.createdAt,
    },
    ...(isOwner
      ? [
          {
            key: "actions",
            header: "",
            align: "right" as const,
            width: 64,
            render: (user: AppUser) => (
              <IconButton
                size="small"
                aria-label={`actions for ${user.email}`}
                onClick={(event) => {
                  setMenuAnchor(event.currentTarget);
                  setMenuUser(user);
                }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            ),
          },
        ]
      : []),
  ];

  if (status === "loading" && users.length === 0) {
    return <FullPageStatus variant="loading" title="Loading users..." />;
  }

  if (status === "failed" && users.length === 0) {
    return (
      <FullPageStatus
        variant="error"
        title="Could not load users"
        description={error ?? undefined}
      />
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Users
        </Typography>
        {isOwner ? (
          <Button variant="contained" onClick={() => setInviteOpen(true)}>
            Invite user
          </Button>
        ) : null}
      </Stack>

      <Input
        label="Search by email or name"
        value={search}
        onChange={(event) => handleSearch(event.target.value)}
        sx={{ mb: 3, maxWidth: 360 }}
      />

      <DataTable<AppUser>
        columns={columns}
        rows={users}
        getRowId={(user) => user.id}
        loading={status === "loading"}
        emptyMessage="No users found."
      />

      {!isOwner ? (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
          Only the Owner can activate/deactivate accounts or grant/revoke Owner or Viewer status.
        </Typography>
      ) : null}

      <InviteUserModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onConfirm={handleInvite}
      />

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        options={[
          {
            key: "assign-team",
            label: "Assign to team",
            onSelect: () => menuUser && setAssignTarget(menuUser),
          },
        ]}
      />

      <AssignTeamRoleModal
        open={assignTarget !== null}
        onClose={() => setAssignTarget(null)}
        userLabel={assignTarget?.displayName ?? assignTarget?.email ?? ""}
        teams={teams}
        onConfirm={handleAssignTeamRole}
      />
    </Box>
  );
}
